import { useRef, useEffect, useCallback, useMemo, useState } from "react";
import { colors, type Node, type GridConfig } from "../theme";
import { Cube, GridLines } from "../components";

interface HeroProps {
  isDark: boolean;
  hasInteracted: boolean;
  awakened: Set<number>;
  activeNode: number;
  onNodeHover: (nodeId: number) => void;
  onNodeLeave: () => void;
  onFirstInteraction: (nodes: Node[], gridConfig: GridConfig) => void;
  onActivity: () => void;
  onToggleIdle: () => void;
  onOpenWaitlist: (presets?: {
    interest?: string[];
    betaTester?: boolean;
    contactMe?: boolean;
  }) => void;
  onOpenArchitecture: () => void;
}

export function Hero({
  isDark,
  hasInteracted,
  awakened,
  activeNode,
  onNodeHover,
  onNodeLeave,
  onFirstInteraction,
  onActivity,
  onToggleIdle,
  onOpenWaitlist,
  onOpenArchitecture,
}: HeroProps) {
  const heroRef = useRef<HTMLElement>(null);
  const [dimensions, setDimensions] = useState({ width: 1200, height: 800 });

  const spacing = 132; // Reduced density
  const padding = 60;
  const navHeight = 90; // Account for fixed nav

  const gridConfig = useMemo(() => {
    const cols = Math.max(
      3,
      Math.floor((dimensions.width - padding * 2) / spacing) + 1
    );
    const rows = Math.max(
      3,
      Math.floor((dimensions.height - padding * 2 - navHeight) / spacing) + 1
    );

    const totalWidth = (cols - 1) * spacing;
    const totalHeight = (rows - 1) * spacing;
    const startX = (dimensions.width - totalWidth) / 2;
    const startY =
      navHeight + (dimensions.height - navHeight - totalHeight) / 2;

    return { cols, rows, startX, startY };
  }, [dimensions]);

  const nodes = useMemo(() => {
    const { cols, rows, startX, startY } = gridConfig;
    return Array.from({ length: rows * cols }, (_, i) => ({
      id: i,
      row: Math.floor(i / cols),
      col: i % cols,
      x: startX + (i % cols) * spacing,
      y: startY + Math.floor(i / cols) * spacing,
    }));
  }, [gridConfig]);

  useEffect(() => {
    const updateDimensions = () => {
      if (heroRef.current) {
        setDimensions({
          width: heroRef.current.offsetWidth,
          height: heroRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent | { clientX: number; clientY: number }) => {
      if (!heroRef.current) return;

      const rect = heroRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      let closestDist = Infinity;
      let closestNode = -1;
      nodes.forEach((node, i) => {
        const dist = Math.sqrt(
          Math.pow(x - node.x, 2) + Math.pow(y - node.y, 2)
        );
        if (dist < closestDist && dist < 50) {
          closestDist = dist;
          closestNode = i;
        }
      });

      if (closestNode >= 0) {
        onNodeHover(closestNode);
      } else {
        onNodeLeave();
      }

      if (!hasInteracted) {
        onFirstInteraction(nodes, gridConfig);
      }

      onActivity();
    },
    [hasInteracted, nodes, gridConfig, onNodeHover, onNodeLeave, onFirstInteraction, onActivity]
  );

  return (
    <section
      ref={heroRef}
      className="relative h-screen flex items-center justify-center overflow-hidden"
      onMouseMove={handleMouseMove}
      onTouchStart={() => {
        if (!hasInteracted) {
          handleMouseMove({
            clientX: window.innerWidth / 2,
            clientY: window.innerHeight / 2,
          });
        }
      }}
      style={{ perspective: "1000px" }}
    >
      {/* SVG Grid lines connecting nodes */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <GridLines
          nodes={nodes}
          cols={gridConfig.cols}
          isDark={isDark}
          awakened={awakened}
        />
      </svg>

      {/* Cube nodes */}
      <div className="absolute inset-0" style={{ zIndex: 2 }}>
        {nodes.map((node, i) => (
          <Cube
            key={node.id}
            x={node.x}
            y={node.y}
            size={43}
            isAwake={awakened.has(i)}
            isActive={activeNode === i}
            isDark={isDark}
          />
        ))}
      </div>

      {/* Hero content - centered overlay */}
      <div
        className="relative z-20 text-center px-8 py-16 max-w-3xl mx-auto rounded-3xl transition-colors duration-[2000ms]"
        style={{
          backgroundColor: isDark ? `${colors.base03}E6` : `${colors.base3}E6`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${isDark ? colors.base02 : colors.base2}`,
        }}
      >
        {/* Pre-interaction prompt */}
        <div
          className={`transition-all duration-700 ${hasInteracted ? "opacity-0 -translate-y-4 pointer-events-none absolute inset-x-0" : "opacity-100"}`}
          style={{ color: isDark ? colors.base01 : colors.base1 }}
        >
          <p className="text-lg mb-4 tracking-wide font-light">
            [ move to turn on the lights ]
          </p>
        </div>

        {/* Main headline */}
        <h1
          className={`text-5xl md:text-7xl font-semibold tracking-tight mb-6 transition-all duration-700 ${hasInteracted ? "opacity-100 translate-y-0" : "opacity-30"}`}
          style={{ lineHeight: 1.1 }}
        >
          <span
            className={isDark ? "text-gradient-dark" : "text-gradient-light"}
          >
            We turn off the lights
          </span>
          <br />
          <span style={{ color: isDark ? colors.base0 : colors.base00 }}>
            so your data can rest
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className={`text-xl transition-all duration-700 delay-200 ${hasInteracted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ color: isDark ? colors.base01 : colors.base1 }}
        >
          Archive storage that costs less the longer you keep it.
          <br />
          <span style={{ color: colors.magenta }}>
            You save. We save. The planet saves.
          </span>
        </p>

        {/* Mode indicator */}
        <button
          onClick={onToggleIdle}
          className={`mt-8 inline-flex items-center gap-3 px-4 py-2 rounded-full transition-all duration-700 delay-300 cursor-pointer hover:scale-105 ${hasInteracted ? "opacity-100" : "opacity-0"}`}
          style={{
            backgroundColor: isDark ? colors.base02 : colors.base2,
            border: `1px solid ${isDark ? colors.base01 : colors.base1}20`,
          }}
        >
          <div
            className="w-2 h-2 rounded-full transition-colors duration-[2000ms]"
            style={{ backgroundColor: isDark ? colors.cyan : colors.magenta }}
          />
          <span
            className="text-sm font-medium"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {isDark ? "lights off" : "lights on"}
          </span>
        </button>

        {/* CTA buttons */}
        <div
          className={`mt-10 flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-500 ${hasInteracted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <button
            onClick={() => onOpenWaitlist({ betaTester: true })}
            className="px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: isDark ? colors.cyan : colors.blue,
              color: isDark ? colors.base03 : colors.base3,
              boxShadow: isDark
                ? `0 4px 20px ${colors.cyan}40`
                : `0 4px 20px ${colors.blue}30`,
            }}
          >
            Request Early Access
          </button>
          <button
            onClick={onOpenArchitecture}
            className="px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: isDark ? `${colors.base02}` : `${colors.base2}`,
              border: `1px solid ${isDark ? colors.base01 : colors.base1}30`,
              color: isDark ? colors.base0 : colors.base00,
            }}
          >
            View Architecture →
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-20 transition-all duration-700 delay-1000 ${hasInteracted ? "opacity-100" : "opacity-0"}`}
      >
        <div
          className="flex flex-col items-center gap-2 px-4 py-3 rounded-full transition-colors duration-[2000ms]"
          style={{
            backgroundColor: isDark
              ? `${colors.base03}CC`
              : `${colors.base3}CC`,
            backdropFilter: "blur(8px)",
            color: isDark ? colors.base01 : colors.base1,
          }}
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div
            className="w-px h-6"
            style={{
              background: `linear-gradient(to bottom, ${isDark ? colors.base01 : colors.base1}, transparent)`,
            }}
          />
        </div>
      </div>
    </section>
  );
}
